import type { MouseEvent, ReactNode } from 'react'

import { cn } from '@/ui/lib/utils'
import { TONE_CLASS, type ReturnTone } from '@/ui/lib/quantFormat'

export interface QuantStrategyListItem {
  slug: string
  /** Caps display name (a broken strategy shows its slug). */
  name: string
  /** Mono second line (a broken strategy shows `strategy.toml`). */
  subtitle: string
  /** `N runs · 1 running`; empty for a broken strategy. */
  flag: string
  configError: boolean
  /** Formatted return (`+4.62%`, true minus) or `—` when none. */
  returnText: string
  returnTone: ReturnTone
  live: boolean
  selected: boolean
  href: string
}

export interface QuantStrategyListProps {
  items: QuantStrategyListItem[]
  /** Busy run slots and the cap; both absent until the runs list has loaded (no slot count is shown before). */
  active?: number
  max?: number
  /** Every slot is busy and none is the caller's (spec 038 19.6); changes the footnote only. */
  heldByOther?: boolean
  /** Router-aware navigation supplied by the caller. */
  onNavigate: (href: string) => void
  /** Optional line above the slot meter (e.g. a runs-unavailable notice). */
  notice?: ReactNode
}

/**
 * The strategy list column (spec 036 handoff 3.1): a label with the on-disk
 * count, fixed-height rows (real `<a>` links, selected = paper/ink) in a scrolling
 * list, and the run-slots meter pinned at the bottom so it stays visible. Presentational: navigation arrives as a prop.
 */
export function QuantStrategyList({ items, active, max, heldByOther = false, onNavigate, notice }: QuantStrategyListProps) {
  function handleClick(event: MouseEvent<HTMLAnchorElement>, href: string) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return
    event.preventDefault()
    onNavigate(href)
  }

  return (
    <aside className="flex min-h-0 flex-col gap-2 border-r border-paper-14 px-4 pt-4 pb-[18px]">
      <div className="flex shrink-0 items-baseline gap-2.5 pb-1">
        <span className="font-mono text-[9.5px] tracking-[0.2em] text-accent-line uppercase">Strategies</span>
        <span aria-hidden="true" className="h-px flex-1 bg-paper-14" />
        <span className="font-mono text-[9.5px] tracking-[0.14em] text-paper-40 uppercase">
          {items.length} on disk
        </span>
      </div>
      <ul aria-label="Strategy list" className="m-0 flex min-h-0 flex-1 list-none flex-col gap-2 overflow-y-auto p-0">
        {items.map((item) => (
          <li key={item.slug} className="shrink-0">
            <a
              href={item.href}
              onClick={(event) => handleClick(event, item.href)}
              aria-current={item.selected ? 'page' : undefined}
              data-strategy-slug={item.slug}
              className={cn(
                'flex h-[72px] flex-col justify-between overflow-hidden border px-3 py-2.5',
                item.selected ? 'border-paper bg-paper text-ink' : 'border-paper-14 bg-transparent text-paper-90',
              )}
            >
              <span className="flex items-center justify-between gap-2">
                <span
                  className="min-w-0 truncate text-left font-sans text-[12px] font-bold tracking-[0.14em] uppercase"
                  title={item.name}
                >
                  {item.name}
                </span>
                {item.live && (
                  <span
                    className={cn(
                      'flex shrink-0 items-center gap-[5px] font-mono text-[9px] tracking-[0.14em] uppercase',
                      item.selected ? 'text-accent-fill' : 'text-accent-line',
                    )}
                  >
                    <i
                      aria-hidden="true"
                      className={cn(
                        'block h-1.5 w-1.5 animate-pulse',
                        item.selected ? 'bg-accent-fill' : 'bg-accent-line',
                      )}
                    />
                    Live
                  </span>
                )}
              </span>
              <span className="truncate text-left font-mono text-[11px] opacity-65" title={item.subtitle}>
                {item.subtitle}
              </span>
              <span className="flex justify-between gap-2 font-mono text-[10px] tracking-[0.1em] uppercase">
                {item.configError ? (
                  <span className="truncate text-alert">Config error</span>
                ) : (
                  <span className="truncate opacity-60">{item.flag}</span>
                )}
                <span className={cn('shrink-0', item.selected ? 'text-ink' : TONE_CLASS[item.returnTone])}>
                  {item.returnText}
                </span>
              </span>
            </a>
          </li>
        ))}
      </ul>
      <div
        data-held-by-other={heldByOther ? 'true' : undefined}
        className="flex shrink-0 flex-col gap-2 border-t border-paper-14 pt-3.5"
      >
        {notice}
        <div className="flex items-baseline justify-between">
          <span className="font-sans text-[10px] font-semibold tracking-[0.2em] text-paper-50 uppercase">
            Run slots
          </span>
          <span className="h-4 font-mono text-[11px] text-paper-90">
            {max !== undefined && active !== undefined ? `${active} / ${max} busy` : ''}
          </span>
        </div>
        <div aria-hidden="true" data-run-slots className="flex h-2 gap-1">
          {max !== undefined &&
            active !== undefined &&
            Array.from({ length: max }, (_, i) => (
              <span
                key={i}
                data-slot-busy={i < active ? 'true' : 'false'}
                className={cn('block h-2 flex-1', i < active ? 'bg-accent-line' : 'border border-paper-35')}
              />
            ))}
        </div>
        <div data-slot-footnote className="h-[33px] overflow-hidden font-mono text-[11px] leading-[1.5] text-paper-40">
          {heldByOther ? 'Held by another user. Starts are refused.' : 'Runs past the cap are refused, never queued.'}
        </div>
      </div>
    </aside>
  )
}
